// src/pages/ClientManagement.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "../api";
import { ui, uiHandlers } from "../uiStyles";

function money(x) {
  const n = Number(x || 0);
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d);
  }
}

export default function ClientManagement() {
  const [clients, setClients] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState("newest");
  const [showArchived, setShowArchived] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archivedHits, setArchivedHits] = useState([]);
  const [fileCard, setFileCard] = useState(null);
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeTab, setActiveTab] = useState("overview");

  const [clientJobs, setClientJobs] = useState([]);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let alive = true;

    async function loadClients() {
      setLoading(true);
      setError("");

      try {
        const [clientData, jobsData] = await Promise.all([
          apiRequest(showArchived ? "/clients?archived=true" : "/clients"),
          apiRequest("/jobs/").catch(() => []),
        ]);
        if (!alive) return;

        const list = Array.isArray(clientData) ? clientData : [];
        setClients(list);
        setAllJobs(Array.isArray(jobsData) ? jobsData : []);
      } catch (err) {
        const msg = err?.message || "Failed to load clients";

        if (String(msg).toLowerCase().includes("unauthorized")) {
          if (alive) setError("Unauthorized while loading clients.");
          return;
        }

        if (alive) setError(msg);
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadClients();
    return () => {
      alive = false;
    };
  }, [showArchived]);

  useEffect(() => {
    const query = search.trim().toLowerCase();
    if (!query || showArchived) {
      setArchivedHits([]);
      return undefined;
    }
    let alive = true;
    apiRequest("/clients?archived=true")
      .then((rows) => {
        if (!alive) return;
        const hits = (Array.isArray(rows) ? rows : []).filter((client) => {
          const haystack = [
            client.company_name,
            client.contact_name,
            client.email,
            client.phone,
            client.id,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(query);
        });
        setArchivedHits(hits);
      })
      .catch(() => {
        if (alive) setArchivedHits([]);
      });
    return () => {
      alive = false;
    };
  }, [search, showArchived]);

  async function openClientFile(client) {
    setFileCard({ loading: true, client });
    try {
      const file = await apiRequest(`/clients/${client.id}/file`);
      setFileCard(file);
    } catch (err) {
      setError(err?.message || "Could not open this client file.");
      setFileCard(null);
    }
  }

  const lastWorkedByClient = useMemo(() => {
    const latest = new Map();

    allJobs.forEach((job) => {
      const clientId = Number(job.client_id);
      const workedAt = new Date(job.date_created || 0).getTime();
      if (!clientId || !Number.isFinite(workedAt)) return;

      latest.set(clientId, Math.max(latest.get(clientId) || 0, workedAt));
    });

    return latest;
  }, [allJobs]);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();

    const matching = clients.filter((c) => {
      if (!q) return true;
      const haystack = [
        c.company_name,
        c.contact_name,
        c.email,
        c.phone,
        c.id?.toString(),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });

    return [...matching].sort((a, b) => {
      if (sortMode === "alphabetical") {
        const aName = a.company_name || a.contact_name || `Client ${a.id}`;
        const bName = b.company_name || b.contact_name || `Client ${b.id}`;
        return aName.localeCompare(bName, undefined, { sensitivity: "base" });
      }

      if (sortMode === "last_worked") {
        const aWorked = lastWorkedByClient.get(Number(a.id)) || 0;
        const bWorked = lastWorkedByClient.get(Number(b.id)) || 0;
        return bWorked - aWorked || Number(b.id) - Number(a.id);
      }

      return Number(b.id) - Number(a.id);
    });
  }, [clients, lastWorkedByClient, search, sortMode]);

  const selected =
    filteredClients.find((c) => c.id === selectedId) ||
    clients.find((c) => c.id === selectedId) ||
    null;

  useEffect(() => {
    let alive = true;

    async function loadClientDetails() {
      if (!selected?.id) {
        setClientJobs([]);
        setClientInvoices([]);
        return;
      }

      setDetailLoading(true);

      try {
        const [jobsData, invoiceData] = await Promise.all([
          apiRequest("/jobs/").catch(() => []),
          apiRequest(`/invoices?client_id=${selected.id}`).catch(() => []),
        ]);

        if (!alive) return;

        const jobsList = Array.isArray(jobsData)
          ? jobsData.filter((j) => Number(j.client_id) === Number(selected.id))
          : [];

        const invoiceList = Array.isArray(invoiceData) ? invoiceData : [];

        setClientJobs(jobsList);
        setClientInvoices(invoiceList);
      } finally {
        if (alive) setDetailLoading(false);
      }
    }

    loadClientDetails();
    return () => {
      alive = false;
    };
  }, [selected?.id]);

  const invoiceCount = clientInvoices.length;
  const jobsCount = clientJobs.length;
  const outstandingTotal = clientInvoices.reduce((sum, inv) => {
    const total = Number(inv.total || 0);
    const paid = Number(inv.amount_paid || 0);
    return sum + Math.max(total - paid, 0);
  }, 0);

  const paidInvoicesCount = clientInvoices.filter((inv) => {
    const total = Number(inv.total || 0);
    const paid = Number(inv.amount_paid || 0);
    return total > 0 && paid >= total;
  }).length;

  async function setClientArchived(next) {
    if (!selected?.id) return;
    setArchiving(true);
    setError("");
    try {
      await apiRequest(`/clients/${selected.id}/archive`, {
        method: "PATCH",
        body: { archived: next },
      });
      setClients((current) => current.filter((client) => client.id !== selected.id));
      setSelectedId(null);
    } catch (err) {
      setError(err?.message || "Could not update this client.");
    } finally {
      setArchiving(false);
    }
  }

  return (
    <div style={ui.page}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, color: "#ffffff", fontSize: 20 }}>
          Clients
        </h2>
        <p style={{ marginTop: 8, fontSize: 14, color: "#9fb0c3" }}>
          Find or create a client, then select the person you want to manage.
        </p>
      </div>

      {error && (
        <div
          style={{
            ...ui.card,
            marginBottom: 16,
            background: "rgba(127, 29, 29, 0.28)",
            border: "1px solid rgba(239, 68, 68, 0.35)",
            color: "#fecaca",
          }}
        >
          {error}
        </div>
      )}

      <div className="jt-client-management-grid">
        {/* CLIENT DIRECTORY - always shown first */}
        <div style={ui.card}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              marginBottom: 14,
              flexWrap: "wrap",
            }}
          >
            <h3 style={{ margin: 0, color: "#ffffff" }}>
              {showArchived ? "Archived" : "Clients"} ({clients.length})
            </h3>

            <Link
              to="/clients/new"
              style={goldOutlineButtonStyle}
              {...uiHandlers.secondaryHover}
            >
              + Create New Client
            </Link>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(170px, 220px)",
              gap: 10,
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <input
              type="text"
              placeholder="Search by name, email, phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={ui.input}
              {...uiHandlers.focus}
            />

            <select
              aria-label="Sort clients"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              style={ui.input}
              {...uiHandlers.focus}
            >
              <option value="newest">Newest first</option>
              <option value="alphabetical">Alphabetical A-Z</option>
              <option value="last_worked">Last worked</option>
            </select>
          </div>

          {archivedHits.length > 0 && (
            <div
              style={{
                ...ui.subCard,
                marginBottom: 14,
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ color: "#f5c542", fontSize: 12, fontWeight: 800 }}>
                Archived matches
              </div>
              {archivedHits.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => openClientFile(client)}
                  style={{
                    textAlign: "left",
                    background: "#0f2235",
                    color: "#ffffff",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 8,
                    padding: "8px 10px",
                    cursor: "pointer",
                    boxShadow: "none",
                  }}
                >
                  {client.company_name || client.contact_name || `Client #${client.id}`}
                  <span style={{ color: "#9fb0c3" }}> · Archived</span>
                </button>
              ))}
            </div>
          )}

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#dbe7f3",
              fontSize: 14,
              marginBottom: 14,
            }}
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                setSelectedId(null);
              }}
              style={{ width: 16, height: 16 }}
            />
            Archived
          </label>

          {loading && <p style={{ color: "#9fb0c3" }}>Loading…</p>}

          {!loading && filteredClients.length === 0 && (
            <p style={{ color: "#9fb0c3" }}>
              {showArchived ? "No archived clients." : "No clients found."}
            </p>
          )}

          <div
            aria-label="Client list"
            style={{ maxHeight: 520, overflowY: "auto", paddingRight: 4 }}
          >
            {filteredClients.map((c) => {
              const isSelected = selected && selected.id === c.id;

              return (
                <div
                  key={c.id}
                  onClick={() => {
                    setSelectedId(c.id);
                    setActiveTab("overview");
                    if (search.trim() || showArchived || c.archived) openClientFile(c);
                  }}
                  style={{
                    ...ui.subCard,
                    marginBottom: 12,
                    cursor: "pointer",
                    background: isSelected ? "#16324a" : ui.subCard.background,
                    border: isSelected
                      ? "1px solid rgba(96,165,250,0.55)"
                      : ui.subCard.border,
                    boxShadow: isSelected
                      ? "0 0 0 1px rgba(96,165,250,0.25), 0 6px 18px rgba(0,0,0,0.25)"
                      : "none",
                    transition: "all 0.18s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    if (!isSelected) {
                      e.currentTarget.style.background = "#143048";
                      e.currentTarget.style.boxShadow =
                        "0 6px 18px rgba(0,0,0,0.18)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    if (!isSelected) {
                      e.currentTarget.style.background = ui.subCard.background;
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                >
                  <div
                    style={{
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: 16,
                      marginBottom: 6,
                    }}
                  >
                    {c.company_name || c.contact_name || `Client #${c.id}`}
                  </div>

                  <div style={{ fontSize: 13, color: "#9fb0c3" }}>
                    {c.email || "No email on file"}
                  </div>

                  <div style={{ fontSize: 13, color: "#9fb0c3", marginTop: 2 }}>
                    {c.phone || "No phone on file"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CLIENT MANAGEMENT - shown below the client directory */}
        <div style={ui.card}>
          {!selected ? (
            <div
              style={{
                minHeight: 280,
                display: "grid",
                placeItems: "center",
                textAlign: "center",
                color: "#9fb0c3",
              }}
            >
              <div>
                <h3 style={{ marginTop: 0, color: "#ffffff" }}>Client Details</h3>
                <p style={{ marginBottom: 0 }}>
                  Select a client from the list to view details.
                </p>
              </div>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <Link
                  to={`/clients/${selected.id}`}
                  style={{ ...goldOutlineButtonStyle, padding: "9px 13px", fontSize: 13 }}
                  {...uiHandlers.secondaryHover}
                >
                  Open Full Detail
                </Link>

                <Link
                  to="/clients-jobs"
                  style={{
                    ...goldOutlineButtonStyle,
                    padding: "9px 13px",
                    fontSize: 13,
                  }}
                  {...uiHandlers.secondaryHover}
                >
                  Open Jobs Workspace
                </Link>

                <button
                  type="button"
                  disabled={archiving}
                  onClick={() => setClientArchived(!showArchived)}
                  style={{
                    background: "transparent",
                    color: "#ef4444",
                    border: "1px solid #ef4444",
                    borderRadius: 8,
                    padding: "9px 13px",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "none",
                  }}
                >
                  {archiving ? "Saving…" : showArchived ? "Unarchive" : "Archive"}
                </button>
              </div>

              {/* Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 16,
                  flexWrap: "wrap",
                  marginBottom: 18,
                }}
              >
                <div>
                  <h3 style={{ margin: 0, color: "#ffffff", fontSize: 22 }}>
                    {selected.company_name || selected.contact_name}
                  </h3>
                  <div style={{ color: "#9fb0c3", fontSize: 14, marginTop: 8 }}>
                    Client ID: {selected.id}
                  </div>
                </div>

              </div>

              {/* Stats */}
              <div className="jt-client-stats-grid">
                <div style={ui.subCard}>
                  <div style={labelStyle}>Email</div>
                  <div style={valueStyle}>{selected.email || "—"}</div>
                </div>

                <div style={ui.subCard}>
                  <div style={labelStyle}>Phone</div>
                  <div style={valueStyle}>{selected.phone || "—"}</div>
                </div>

                <div style={ui.subCard}>
                  <div style={labelStyle}>Jobs</div>
                  <div style={valueStyle}>{jobsCount}</div>
                </div>

                <div style={ui.subCard}>
                  <div style={labelStyle}>Outstanding</div>
                  <div
                    style={{
                      ...valueStyle,
                      color: "#f5c542",
                      fontSize: 20,
                    }}
                  >
                    {money(outstandingTotal || selected.ar_balance || 0)}
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginBottom: 16,
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                  paddingBottom: 14,
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={() => setActiveTab("overview")}
                  style={goldOutlineButtonStyle}
                  {...uiHandlers.secondaryHover}
                >
                  Overview
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("jobs")}
                  style={goldOutlineButtonStyle}
                  {...uiHandlers.secondaryHover}
                >
                  Jobs
                </button>

                <Link
                  to="/estimates"
                  style={goldOutlineButtonStyle}
                  {...uiHandlers.secondaryHover}
                >
                  Estimates
                </Link>

                <button
                  type="button"
                  onClick={() => setActiveTab("invoices")}
                  style={goldOutlineButtonStyle}
                  {...uiHandlers.secondaryHover}
                >
                  Invoices
                </button>
              </div>

              {detailLoading ? (
                <div style={{ color: "#9fb0c3", padding: "16px 0" }}>
                  Loading client activity…
                </div>
              ) : (
                <>
                  {activeTab === "overview" && (
                    <div className="jt-client-overview-grid">
                      <div style={ui.subCard}>
                        <div style={labelStyle}>Client Summary</div>

                        <div style={summaryRowStyle}>
                          <span style={summaryKeyStyle}>Contact Name</span>
                          <span style={summaryValueStyle}>
                            {selected.contact_name || "—"}
                          </span>
                        </div>

                        <div style={summaryRowStyle}>
                          <span style={summaryKeyStyle}>Company</span>
                          <span style={summaryValueStyle}>
                            {selected.company_name || "—"}
                          </span>
                        </div>

                        <div style={summaryRowStyle}>
                          <span style={summaryKeyStyle}>Email</span>
                          <span style={summaryValueStyle}>
                            {selected.email || "—"}
                          </span>
                        </div>

                        <div style={summaryRowStyle}>
                          <span style={summaryKeyStyle}>Phone</span>
                          <span style={summaryValueStyle}>
                            {selected.phone || "—"}
                          </span>
                        </div>

                        <div style={summaryRowStyle}>
                          <span style={summaryKeyStyle}>Invoices</span>
                          <span style={summaryValueStyle}>{invoiceCount}</span>
                        </div>

                        <div style={summaryRowStyle}>
                          <span style={summaryKeyStyle}>Paid In Full</span>
                          <span style={summaryValueStyle}>{paidInvoicesCount}</span>
                        </div>
                      </div>


                    </div>
                  )}

                  {activeTab === "jobs" && (
                    <div style={ui.subCard}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 12,
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={labelStyle}>Jobs ({jobsCount})</div>
                        <Link
                          to="/clients-jobs"
                          style={goldOutlineButtonStyle}
                          {...uiHandlers.secondaryHover}
                        >
                          Open Jobs Workspace
                        </Link>
                      </div>

                      {clientJobs.length === 0 ? (
                        <div style={{ color: "#9fb0c3" }}>
                          No jobs found for this client.
                        </div>
                      ) : (
                        <div style={{ display: "grid", gap: 10 }}>
                          {clientJobs.map((job) => (
                            <div
                              key={job.id}
                              style={{
                                ...ui.subCard,
                                marginBottom: 0,
                                padding: 14,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  gap: 12,
                                  flexWrap: "wrap",
                                  marginBottom: 6,
                                }}
                              >
                                <div style={{ color: "#ffffff", fontWeight: 700 }}>
                                  {job.defendant_name || job.job_name || `Job #${job.id}`}
                                </div>
                                <div style={{ color: "#9fb0c3", fontSize: 13 }}>
                                  {job.status || "Prospect"}
                                </div>
                              </div>

                              <div style={{ color: "#9fb0c3", fontSize: 13 }}>
                                Project #: {job.case_number || "—"}
                              </div>
                              <div style={{ color: "#9fb0c3", fontSize: 13, marginTop: 3 }}>
                                Address: {job.service_address || "—"}
                              </div>

                              <div style={{ marginTop: 10 }}>
                                <Link
                                  to={`/jobs/${job.id}`}
                                  style={goldOutlineButtonStyle}
                                  {...uiHandlers.secondaryHover}
                                >
                                  Open Job
                                </Link>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === "invoices" && (
                    <div style={ui.subCard}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 12,
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <div style={labelStyle}>Invoices ({invoiceCount})</div>
                        <Link
                          to="/invoices"
                          style={goldOutlineButtonStyle}
                          {...uiHandlers.secondaryHover}
                        >
                          Open Invoices
                        </Link>
                      </div>

                      {clientInvoices.length === 0 ? (
                        <div style={{ color: "#9fb0c3" }}>
                          No invoices found for this client.
                        </div>
                      ) : (
                        <div style={{ overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr>
                                <th style={tableTh}>Invoice #</th>
                                <th style={tableTh}>Issue Date</th>
                                <th style={tableTh}>Status</th>
                                <th style={{ ...tableTh, textAlign: "right" }}>Total</th>
                                <th style={{ ...tableTh, textAlign: "right" }}>Paid</th>
                                <th style={{ ...tableTh, textAlign: "right" }}>Balance</th>
                                <th style={tableTh}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientInvoices.map((inv) => {
                                const total = Number(inv.total || 0);
                                const paid = Number(inv.amount_paid || 0);
                                const balance = Math.max(total - paid, 0);

                                return (
                                  <tr key={inv.id}>
                                    <td style={tableTd}>
                                      {inv.invoice_number || `INV-${inv.id}`}
                                    </td>
                                    <td style={tableTd}>{fmtDate(inv.issue_date)}</td>
                                    <td style={tableTd}>{inv.status || "DRAFT"}</td>
                                    <td style={{ ...tableTd, textAlign: "right" }}>
                                      {money(total)}
                                    </td>
                                    <td style={{ ...tableTd, textAlign: "right" }}>
                                      {money(paid)}
                                    </td>
                                    <td
                                      style={{
                                        ...tableTd,
                                        textAlign: "right",
                                        color: balance > 0 ? "#f5c542" : "#86efac",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {money(balance)}
                                    </td>
                                    <td style={tableTd}>
                                      <Link
                                        to={`/invoices/${inv.id}`}
                                        style={goldOutlineButtonStyle}
                                        {...uiHandlers.secondaryHover}
                                      >
                                        Open
                                      </Link>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>


      </div>
      {fileCard && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(2, 8, 16, 0.72)",
            display: "grid",
            placeItems: "center",
            padding: 16,
            zIndex: 40,
          }}
          onClick={() => setFileCard(null)}
        >
          <div
            style={{
              ...ui.card,
              width: "min(680px, 100%)",
              maxHeight: "86vh",
              overflowY: "auto",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {fileCard.loading ? (
              <p style={{ color: "#dbe7f3" }}>Opening client file…</p>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                  <div>
                    <div style={{ color: "#f5c542", fontSize: 12, fontWeight: 800 }}>
                      {fileCard.client.archived ? "ARCHIVED CLIENT" : "CLIENT FILE"}
                    </div>
                    <h3 style={{ margin: "6px 0", color: "#ffffff" }}>
                      {fileCard.client.company_name || fileCard.client.contact_name || `Client #${fileCard.client.id}`}
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFileCard(null)}
                    style={{ ...goldOutlineButtonStyle, padding: "8px 12px" }}
                  >
                    Close
                  </button>
                </div>
                <FileLinks title="Client file" rows={[{
                  href: `/clients/${fileCard.client.id}`,
                  label: "Open client file",
                }]} />
                <FileLinks
                  title="Jobs"
                  rows={(fileCard.jobs || []).map((job) => ({
                    href: `/jobs/${job.id}`,
                    label: `${job.number}${job.status ? ` · ${job.status}` : ""}`,
                  }))}
                />
                <FileLinks
                  title="Estimates"
                  rows={(fileCard.estimates || []).map((item) => ({
                    href: `/estimates/${item.id}`,
                    label: `${item.number} · ${money(item.total)}`,
                  }))}
                />
                <FileLinks
                  title="Invoices"
                  rows={(fileCard.invoices || []).map((item) => ({
                    href: `/invoices/${item.id}`,
                    label: `${item.number} · ${money(item.total)}`,
                  }))}
                />
                <FileLinks
                  title="Receipts"
                  rows={(fileCard.receipts || []).map((item) => ({
                    href: `/invoices/${item.invoice_id}/payments/${item.id}/receipt`,
                    label: `${item.number} · ${money(item.amount)}`,
                  }))}
                />
                {fileCard.client.archived && (
                <button
                  type="button"
                  disabled={archiving}
                  onClick={async () => {
                    setArchiving(true);
                    setError("");
                    try {
                      await apiRequest(`/clients/${fileCard.client.id}/archive`, {
                        method: "PATCH",
                        body: { archived: false },
                      });
                      setArchivedHits((current) => current.filter((client) => client.id !== fileCard.client.id));
                      setClients((current) => current.filter((client) => client.id !== fileCard.client.id));
                      setFileCard(null);
                      setSelectedId(null);
                    } catch (err) {
                      setError(err?.message || "Could not unarchive this client.");
                    } finally {
                      setArchiving(false);
                    }
                  }}
                  style={{
                    marginTop: 16,
                    background: "transparent",
                    color: "#ef4444",
                    border: "1px solid #ef4444",
                    borderRadius: 8,
                    padding: "9px 13px",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "none",
                  }}
                >
                  {archiving ? "Saving…" : "Unarchive"}
                </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FileLinks({ title, rows }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ color: "#dbe7f3", fontWeight: 800, marginBottom: 6 }}>{title}</div>
      {rows.length === 0 ? (
        <div style={{ color: "#9fb0c3", fontSize: 13 }}>None</div>
      ) : (
        <div style={{ display: "grid", gap: 6 }}>
          {rows.map((row) => (
            <Link key={row.href} to={row.href} style={{ color: "#7dd3fc", fontWeight: 700 }}>
              {row.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const goldOutlineButtonStyle = {
  ...ui.btnSecondary,
  color: "#f5c542",
  border: "1px solid #f5c542",
  background: "rgba(245, 197, 66, 0.08)",
  boxShadow: "0 0 0 1px rgba(245, 197, 66, 0.08)",
};

const labelStyle = {
  fontSize: 12,
  color: "#9fb0c3",
  marginBottom: 8,
  textTransform: "uppercase",
  letterSpacing: 0.3,
};

const valueStyle = {
  fontSize: 18,
  fontWeight: 700,
  color: "#ffffff",
};

const summaryRowStyle = {
  display: "grid",
  gridTemplateColumns: "140px 1fr",
  gap: 12,
  padding: "8px 0",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
};

const summaryKeyStyle = {
  color: "#9fb0c3",
  fontSize: 13,
};

const summaryValueStyle = {
  color: "#ffffff",
  fontSize: 14,
};

const tableTh = {
  textAlign: "left",
  padding: "10px 8px",
  borderBottom: "1px solid rgba(255,255,255,0.10)",
  fontWeight: 700,
  fontSize: 13,
  color: "#dbe7f3",
  whiteSpace: "nowrap",
};

const tableTd = {
  padding: "10px 8px",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
  fontSize: 14,
  color: "#ffffff",
  verticalAlign: "top",
};
